import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  Check, CheckCircle2, Clock, CreditCard, Crown, Loader2,
  Sparkles, TrendingUp, XCircle, Mail, Download, Receipt, FileText,
} from "lucide-react";

// ---------------------------------------------------------------------------
// SubscriptionTab — tenant-facing view of their current plan, usage meters
// and the upgrade ladder. There is no self-serve checkout yet (Stripe is on
// the backlog) — the "Contact sales" CTA opens a mailto: to upgrade the tier
// via the super-admin.
// ---------------------------------------------------------------------------
function fmtSGD(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(Number(n));
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

// FSMS-specific usage keys we surface as meters.
const FSMS_METERS = [
  { key: "max_sites",         label: "Sites" },
  { key: "max_users",         label: "Team members" },
  { key: "max_ccps_per_site", label: "CCPs per site" },
];

export default function SubscriptionTab() {
  const dialog = useDialog();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [feat, tiers, sitesRes, usersRes, ccpsRes, billingRes] = await Promise.all([
        api.get("/tenant/features"),
        api.get("/tenant/plans").catch(() => ({ data: { tiers: [] } })),
        api.get("/sites").catch(() => ({ data: { sites: [] } })),
        api.get("/tenant/directory").catch(() => ({ data: { users: [] } })),
        api.get("/ccps").catch(() => ({ data: { ccps: [] } })),
        api.get("/tenant/billing").catch(() => ({ data: null })),
      ]);
      const sites = sitesRes.data.sites || [];
      const users = (usersRes.data.users || []).filter((u) => u.role !== "super_admin");
      const ccps = ccpsRes.data.ccps || [];
      const ccpsPerSite = sites.length ? Math.ceil(ccps.length / sites.length) : ccps.length;
      setData({
        features: feat.data.features || {},
        tier_name: feat.data.tier_name || "",
        trial_expires_at: feat.data.trial_expires_at || null,
        billing_mode: feat.data.billing_mode || "trial",
        billing: billingRes.data || null,
        all_tiers: tiers.data.tiers || [],
        usage: {
          max_sites: { used: sites.length },
          max_users: { used: users.length },
          max_ccps_per_site: { used: ccpsPerSite },
        },
      });
    } catch (e) {
      dialog.alert?.({ title: "Could not load subscription", message: formatErr(e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadInvoice = async (p) => {
    try {
      const res = await api.get(`/tenant/billing/payments/${p.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${(p.invoice_number || p.id.slice(0, 8)).replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { dialog.alert?.({ title: "Download failed", message: formatErr(e.response?.data?.detail) }); }
  };

  if (loading) {
    return (
      <div className="vb-card p-7 flex items-center gap-2 text-sm" data-testid="sub-loading">
        <Loader2 size={14} className="animate-spin" /> Loading subscription…
      </div>
    );
  }
  if (!data) return null;

  // ---- Invoiced (manual annual billing) tenants get a different view ----
  if (data.billing_mode === "invoiced") {
    return <InvoicedView data={data} downloadInvoice={downloadInvoice} />;
  }

  // Derive state pill — Trial vs Active.
  const trialEnd = data.trial_expires_at;
  const trialIn = daysUntil(trialEnd);
  let state;
  if ((data.tier_name || "").toLowerCase() === "trial" && trialEnd) {
    if (trialIn === 0) state = { label: "Trial expired", color: "var(--vb-error)", Icon: XCircle };
    else state = { label: "Trial", color: "var(--vb-text-2)", Icon: Clock };
  } else {
    state = { label: "Active", color: "var(--vb-accent)", Icon: CheckCircle2 };
  }
  const StateIcon = state.Icon;

  // Pull the live tier metadata if the admin/tiers list was reachable.
  const tiers = data.all_tiers.filter((t) => t.is_active);
  const currentTier = tiers.find((t) => t.name === data.tier_name) || { name: data.tier_name, features: data.features, price_monthly: null };

  const requestUpgrade = (tier) => {
    const subject = encodeURIComponent(`Upgrade request: ${tier.name}`);
    const body = encodeURIComponent(`Hi Vanguard Beacon team,\n\nWe'd like to upgrade our subscription to the ${tier.name} plan.\n\nThanks.`);
    window.location.href = `mailto:vanguardbeacon@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="subscription-tab">
      {/* ---------- Current plan card ---------- */}
      <div className="vb-card p-7">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
          <div className="flex items-center gap-2">
            <CreditCard size={20} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Current plan</h2>
          </div>
          <span className="vb-pill inline-flex items-center gap-1 text-xs"
            style={{ background: "var(--vb-bg-2)", color: state.color }}
            data-testid="sub-state">
            <StateIcon size={12} /> {state.label}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          <div>
            <SubLabel>Plan</SubLabel>
            <div className="text-2xl vb-heading m-0" data-testid="sub-tier-name">{currentTier.name || "—"}</div>
            <div className="text-xs opacity-60 mt-0.5">{currentTier.description || ""}</div>
          </div>
          <div>
            <SubLabel>Monthly price</SubLabel>
            <div className="text-2xl vb-heading m-0">{fmtSGD(currentTier.price_monthly)}</div>
            <div className="text-xs opacity-60 mt-0.5">SGD, GST-exclusive</div>
          </div>
          <div>
            <SubLabel>{state.label === "Trial" ? "Trial ends" : "Next renewal"}</SubLabel>
            <div className="text-2xl vb-heading m-0">{fmtDate(trialEnd)}</div>
            <div className="text-xs opacity-60 mt-0.5">
              {state.label === "Trial" && trialIn != null && `${trialIn} day${trialIn === 1 ? "" : "s"} remaining`}
              {state.label === "Active" && "Managed by your account manager"}
              {state.label === "Trial expired" && "Contact sales to continue using Vanguard Beacon."}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Usage meters ---------- */}
      <div className="vb-card p-7">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} style={{ color: "var(--vb-text-2)" }} />
          <h3 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Usage this period</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {FSMS_METERS.map(({ key, label }) => (
            <UsageBar key={key} label={label}
              used={data.usage[key]?.used ?? 0}
              cap={data.features[key] ?? 0} />
          ))}
        </div>
      </div>

      {/* ---------- Available plans ---------- */}
      <div className="vb-card p-7">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} style={{ color: "var(--vb-text-2)" }} />
          <h3 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Available plans</h3>
        </div>
        <p className="text-sm opacity-70 mb-5">
          Self-serve checkout is coming soon. In the meantime, reach out to your account manager and we'll upgrade your workspace.
        </p>
        {tiers.length === 0 ? (
          <div className="text-sm opacity-60">Plan catalog is being prepared. Contact sales for early upgrade access.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {tiers.map((t) => {
              const isCurrent = t.name === currentTier.name;
              return (
                <div key={t.id || t.name}
                  className="vb-card p-5 flex flex-col"
                  style={{
                    borderWidth: 2,
                    borderColor: isCurrent ? "var(--vb-accent)" : "var(--vb-border)",
                  }}
                  data-testid={`sub-tier-${(t.name || "").toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="vb-heading text-lg">{t.name}</div>
                    {(t.name || "").toLowerCase() === "enterprise" && <Crown size={16} style={{ color: "var(--vb-accent)" }} />}
                  </div>
                  <div className="text-3xl vb-heading mb-1">{fmtSGD(t.price_monthly)}</div>
                  <div className="text-xs opacity-60 mb-3">per month, SGD</div>
                  <div className="text-sm opacity-80 flex-1 min-h-[3rem] mb-4">{t.description}</div>
                  <ul className="text-xs space-y-1 mb-4">
                    <Bullet>{capLabel(t.features?.max_sites)} sites</Bullet>
                    <Bullet>{capLabel(t.features?.max_users)} team members</Bullet>
                    <Bullet>{capLabel(t.features?.max_ccps_per_site)} CCPs per site</Bullet>
                    {t.features?.email_alerts_enabled && <Bullet>Email alerts for deviations</Bullet>}
                    {t.features?.priority_support && <Bullet>Priority support</Bullet>}
                    {t.features?.knowledge_base_enabled && <Bullet>AI Knowledge Base</Bullet>}
                  </ul>
                  {isCurrent ? (
                    <button className="vb-btn ghost w-full inline-flex items-center justify-center gap-1" disabled data-testid={`sub-cta-${(t.name || "").toLowerCase()}`}>
                      <Check size={14} /> Current plan
                    </button>
                  ) : (
                    <button className="vb-btn w-full inline-flex items-center justify-center gap-1"
                      onClick={() => requestUpgrade(t)}
                      data-testid={`sub-cta-${(t.name || "").toLowerCase()}`}>
                      <Mail size={14} /> Contact sales
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs opacity-60 px-2">
        Payments will be processed securely by Stripe once self-serve checkout launches. You can cancel any time before the next renewal.
      </p>
    </div>
  );
}

function capLabel(n) {
  if (n == null) return "—";
  if (n >= 9999) return "Unlimited";
  return n.toLocaleString();
}

function SubLabel({ children }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-wider opacity-60 mb-1">{children}</div>
  );
}

function UsageBar({ label, used, cap }) {
  const numCap = cap >= 9999 ? null : cap;
  const pct = numCap ? Math.min(100, Math.round((used / Math.max(1, numCap)) * 100)) : 0;
  const color = pct >= 95 ? "var(--vb-error)" : pct >= 80 ? "var(--vb-warning)" : "var(--vb-accent)";
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="opacity-70">{used.toLocaleString()} / {numCap == null ? "∞" : numCap.toLocaleString()}</span>
      </div>
      <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "var(--vb-bg-2)" }}>
        <div className="h-full transition-all"
          style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-1.5">
      <Check size={12} className="mt-0.5 flex-shrink-0" style={{ color: "var(--vb-accent)" }} />
      <span>{children}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// InvoicedView — for customers on manual annual invoicing. Shows payment
// status (paid on / next renewal), usage meters, and a downloadable record of
// their invoices instead of the self-serve subscription ladder.
// ---------------------------------------------------------------------------
function InvoicedView({ data, downloadInvoice }) {
  const billing = data.billing || {};
  const validUntil = billing.valid_until || data.trial_expires_at;
  const renewIn = daysUntil(validUntil);
  const lapsed = validUntil && renewIn === 0;
  const payments = billing.payments || [];

  return (
    <div className="space-y-6" data-testid="subscription-tab-invoiced">
      {/* ---------- Plan + payment status ---------- */}
      <div className="vb-card p-7">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Receipt size={20} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Your subscription</h2>
          </div>
          <span className="vb-pill inline-flex items-center gap-1 text-xs"
            style={{ background: "var(--vb-bg-2)", color: lapsed ? "var(--vb-error)" : "var(--vb-accent)" }}
            data-testid="sub-inv-state">
            {lapsed ? <XCircle size={12} /> : <CheckCircle2 size={12} />} {lapsed ? "Renewal due" : "Active"}
          </span>
        </div>
        <p className="text-sm opacity-70 mb-4">Billed annually by invoice, managed by your Vanguard Beacon account manager.</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
          <div>
            <SubLabel>Plan</SubLabel>
            <div className="text-2xl vb-heading m-0" data-testid="sub-inv-plan">{billing.tier_name || data.tier_name || "—"}</div>
          </div>
          <div>
            <SubLabel>Billing</SubLabel>
            <div className="text-2xl vb-heading m-0">Annual</div>
            <div className="text-xs opacity-60 mt-0.5">By invoice</div>
          </div>
          <div>
            <SubLabel>Paid on</SubLabel>
            <div className="text-2xl vb-heading m-0" data-testid="sub-inv-paid-on">{fmtDate(billing.last_paid_on)}</div>
          </div>
          <div>
            <SubLabel>Next renewal</SubLabel>
            <div className="text-2xl vb-heading m-0" style={lapsed ? { color: "var(--vb-error)" } : {}} data-testid="sub-inv-renewal">{fmtDate(validUntil)}</div>
            <div className="text-xs opacity-60 mt-0.5">
              {lapsed ? "Contact us to renew" : renewIn != null ? `in ${renewIn} day${renewIn === 1 ? "" : "s"}` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Usage meters ---------- */}
      <div className="vb-card p-7">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} style={{ color: "var(--vb-text-2)" }} />
          <h3 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Usage this period</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {FSMS_METERS.map(({ key, label }) => (
            <UsageBar key={key} label={label}
              used={data.usage[key]?.used ?? 0}
              cap={data.features[key] ?? 0} />
          ))}
        </div>
      </div>

      {/* ---------- Invoice history ---------- */}
      <div className="vb-card p-7">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} style={{ color: "var(--vb-text-2)" }} />
          <h3 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Invoices</h3>
        </div>
        {payments.length === 0 ? (
          <div className="text-sm opacity-60" data-testid="sub-inv-no-invoices">No invoices on record yet.</div>
        ) : (
          <div className="space-y-2" data-testid="sub-inv-invoice-list">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0"
                style={{ borderColor: "var(--vb-border)" }} data-testid={`sub-inv-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong>{p.invoice_number || "Invoice"}</strong>
                    {p.status === "paid"
                      ? <span className="vb-pill ok text-[10px]">Paid</span>
                      : <span className="vb-pill warn text-[10px]">Pending</span>}
                  </div>
                  <div className="text-xs opacity-70 mt-0.5">
                    {fmtSGD(p.amount)} · {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                  </div>
                </div>
                <button className="vb-btn ghost sm inline-flex items-center gap-1" onClick={() => downloadInvoice(p)}
                  data-testid={`sub-inv-download-${p.id}`}>
                  <Download size={13} /> Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs opacity-60 px-2">
        To change your plan, add sites/seats, or query an invoice, contact your Vanguard Beacon account manager.
      </p>
    </div>
  );
}
